import React, { useState, useEffect } from 'react';
import { 
  Alert, 
  AlertTitle, 
  Box, 
  Button, 
  Collapse,
  Typography,
  IconButton,
  Chip
} from '@mui/material';
import { ExpandMore, ExpandLess, Refresh } from '@mui/icons-material';
import { 
  GuestManagementError, 
  ErrorDisplayInfo,
  useGuestManagementErrorHandler 
} from '../../utils/guest-management-error-handling';

interface ErrorMessageProps {
  title?: string;
  message: string;
  severity?: 'error' | 'warning' | 'info' | 'success';
  onRetry?: () => void;
  error?: GuestManagementError;
  showDetails?: boolean;
  autoRetry?: boolean;
  retryDelay?: number;
}

export const ErrorMessage: React.FC<ErrorMessageProps> = ({ 
  title = 'Error',
  message,
  severity = 'error',
  onRetry,
  error,
  showDetails = false,
  autoRetry = false,
  retryDelay = 0
}) => {
  const [expanded, setExpanded] = useState(false);
  const [retryCountdown, setRetryCountdown] = useState(0);
  const { formatError } = useGuestManagementErrorHandler();

  // Handle auto-retry countdown
  useEffect(() => {
    if (autoRetry && retryDelay > 0 && onRetry) {
      setRetryCountdown(Math.ceil(retryDelay / 1000));
      
      const interval = setInterval(() => {
        setRetryCountdown(prev => {
          if (prev <= 1) {
            clearInterval(interval);
            onRetry();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [autoRetry, retryDelay, onRetry]);

  // Format error if provided
  const errorDisplay: ErrorDisplayInfo | null = error 
    ? formatError(error, onRetry) 
    : null;

  const displayTitle = errorDisplay?.title || title;
  const displayMessage = errorDisplay?.message || message;
  const displaySeverity = errorDisplay?.severity || severity;
  const displayIcon = errorDisplay?.icon;

  return (
    <Box sx={{ my: 2 }}>
      <Alert 
        severity={displaySeverity}
        action={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {error?.retryable && retryCountdown > 0 && (
              <Chip 
                label={`Auto-retry in ${retryCountdown}s`}
                size="small"
                color="primary"
                variant="outlined"
              />
            )}
            {onRetry && !retryCountdown && (
              <Button 
                color="inherit" 
                size="small" 
                onClick={onRetry}
                startIcon={<Refresh />}
                disabled={retryCountdown > 0}
              >
                {errorDisplay?.actionText || 'Retry'}
              </Button>
            )}
            {(showDetails || error?.details) && (
              <IconButton
                color="inherit"
                size="small"
                onClick={() => setExpanded(!expanded)}
              >
                {expanded ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            )}
          </Box>
        }
      >
        <AlertTitle>
          {displayIcon && <span style={{ marginRight: 8 }}>{displayIcon}</span>}
          {displayTitle}
        </AlertTitle>
        
        <Typography variant="body2" component="div">
          {displayMessage.split('\n').map((line, index) => (
            <div key={index}>{line}</div>
          ))}
        </Typography>

        {/* Error Details Collapse */}
        <Collapse in={expanded}>
          <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(0,0,0,0.05)', borderRadius: 1 }}>
            {error?.details && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Technical Details:
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {error.details}
                </Typography>
              </Box>
            )}
            
            {error?.context && Object.keys(error.context).length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Context:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {Object.entries(error.context).map(([key, value]) => (
                    <Chip
                      key={key}
                      label={`${key}: ${String(value)}`}
                      size="small"
                      variant="outlined"
                    />
                  ))}
                </Box>
              </Box>
            )}
            
            {error && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Error Info:
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Type: {error.type}
                  {error.statusCode && ` • Status: ${error.statusCode}`}
                  {error.retryable !== undefined && ` • Retryable: ${error.retryable ? 'Yes' : 'No'}`}
                </Typography>
              </Box>
            )}
          </Box>
        </Collapse>
      </Alert>
    </Box>
  );
};

/**
 * Enhanced error boundary component for guest management operations
 */
interface GuestManagementErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ComponentType<{ error: GuestManagementError; onRetry: () => void }>;
  onError?: (error: GuestManagementError) => void;
}

export const GuestManagementErrorBoundary: React.FC<GuestManagementErrorBoundaryProps> = ({
  children,
  fallback: FallbackComponent,
  onError
}) => {
  const [error, setError] = useState<GuestManagementError | null>(null);
  const { handleError } = useGuestManagementErrorHandler();

  const handleRetry = () => {
    setError(null);
  };

  // Error boundary logic would be implemented here
  // For now, this is a placeholder structure

  if (error) {
    if (FallbackComponent) {
      return <FallbackComponent error={error} onRetry={handleRetry} />;
    }

    return (
      <ErrorMessage
        message={error.userMessage || error.message}
        error={error}
        onRetry={handleRetry}
        showDetails={true}
        autoRetry={error.retryable}
        retryDelay={error.retryAfter ? error.retryAfter * 1000 : 0}
      />
    );
  }

  return <>{children}</>;
};